import React, { useEffect, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, isTypingKey, useSaveGameResult } from './GameShell';
import { playKeyPressSound, playWrongSound, playSuccessSound } from '../../utils/audio';
import { User } from '../../types';

interface Story { title: string; cover: string; scenes: { text: string; art: string }[] }

const STORIES: Story[] = [
  {
    title: 'The Pancake Dragon', cover: '🐉',
    scenes: [
      { text: 'Dex the dragon loved pancakes more than gold.', art: '🐉🥞' },
      { text: 'Every morning, he flipped them with his tail!', art: '🍳💫' },
      { text: 'One day, the honey jar was empty.', art: '🍯😮' },
      { text: 'Dex flew to the bees and asked, "Can I have some honey?"', art: '🐝🐉' },
      { text: 'The bees said yes, and they all shared a giant breakfast.', art: '🥞🎉' },
    ],
  },
  {
    title: 'Pip Goes to the Beach', cover: '🐧',
    scenes: [
      { text: 'Pip the penguin wanted to see a sunny beach.', art: '🐧☀️' },
      { text: 'She packed a towel, a hat, and a bucket of ice.', art: '🎒🧊' },
      { text: 'At the beach, the sand was very hot!', art: '🏖️🔥' },
      { text: 'A crab laughed and said, "Try standing in the water."', art: '🦀😄' },
      { text: 'Pip splashed in the waves and had the best day ever.', art: '🐧🌊' },
    ],
  },
  {
    title: 'The Robot Who Wanted a Pet', cover: '🤖',
    scenes: [
      { text: 'Bolt was a tiny robot who lived in a garage.', art: '🤖🏠' },
      { text: 'He wanted a pet, but every cat ran away from him.', art: '🐱💨' },
      { text: 'So he built a dog out of boxes, tape, and springs.', art: '📦🐕' },
      { text: 'The box dog wagged its tail and went, "Beep, woof!"', art: '🐕✨' },
      { text: 'Now Bolt and Boxy play fetch in the yard every day.', art: '🤖🎾' },
    ],
  },
  {
    title: "The Moon's Lost Shoe", cover: '🌙',
    scenes: [
      { text: 'Long ago, the Moon lost one of her silver shoes.', art: '🌙👟' },
      { text: 'She asked the stars, "Have you seen my shoe?"', art: '⭐❓' },
      { text: 'The stars searched the sky until the sun came up.', art: '✨🌅' },
      { text: 'A little owl found it hanging in an old oak tree.', art: '🦉🌳' },
      { text: 'That night, the Moon smiled so brightly that it looked like day!', art: '🌕😊' },
    ],
  },
  {
    title: 'Shelly Plays Soccer', cover: '🐌',
    scenes: [
      { text: 'Shelly the snail wanted to join the soccer team.', art: '🐌⚽' },
      { text: 'The other players said, "You are much too slow!"', art: '🐰🦊' },
      { text: 'Shelly practiced every day, rain or shine.', art: '🌧️☀️' },
      { text: 'At the big game, she stood in goal and blocked every shot.', art: '🥅🐌' },
      { text: 'Her team won 3 to 2, and everyone cheered for Shelly!', art: '🏆🎉' },
    ],
  },
];

const StoryBuilderGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [storyIndex, setStoryIndex] = useState(0);
  const [scene, setScene] = useState(0);
  const [typed, setTyped] = useState(0);
  const [correctKeys, setCorrectKeys] = useState(0);
  const [errors, setErrors] = useState(0);
  const [flashError, setFlashError] = useState(false);
  const [finishedMs, setFinishedMs] = useState(0);
  const startedAt = useRef<number | null>(null);
  const lastStory = useRef(-1);
  // Read by the key handler so back-to-back keystrokes never see stale state.
  const live = useRef({ scene: 0, typed: 0, done: false });

  const story = STORIES[storyIndex];
  const sentence = story.scenes[scene]?.text ?? '';

  const startGame = () => {
    let next = Math.floor(Math.random() * STORIES.length);
    if (next === lastStory.current) next = (next + 1) % STORIES.length;
    lastStory.current = next;
    setStoryIndex(next);
    live.current = { scene: 0, typed: 0, done: false };
    setScene(0); setTyped(0); setCorrectKeys(0); setErrors(0); setFinishedMs(0);
    startedAt.current = null;
    setGameState('playing');
  };

  const minutes = Math.max(finishedMs, 1) / 60000;
  const wpm = finishedMs ? Math.round(correctKeys / 5 / minutes) : 0;
  const accuracy = correctKeys + errors > 0 ? Math.floor((correctKeys / (correctKeys + errors)) * 100) : 100;

  useSaveGameResult(user, 'story-builder', gameState, wpm);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      if (!isTypingKey(e)) return;
      e.preventDefault();
      const lv = live.current;
      if (lv.done) return;
      const text = story.scenes[lv.scene].text;
      if (startedAt.current === null) startedAt.current = Date.now();
      if (e.key !== text[lv.typed]) {
        setErrors(x => x + 1);
        setFlashError(true);
        setTimeout(() => setFlashError(false), 250);
        playWrongSound();
        return;
      }
      playKeyPressSound();
      setCorrectKeys(c => c + 1);
      if (lv.typed + 1 < text.length) { lv.typed++; setTyped(lv.typed); return; }

      playSuccessSound();
      if (lv.scene + 1 < story.scenes.length) {
        lv.scene++; lv.typed = 0;
        setScene(lv.scene);
        setTyped(0);
      } else {
        lv.done = true;
        setFinishedMs(Date.now() - (startedAt.current ?? Date.now()));
        setGameState('over');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gameState, sentence, typed, scene, story.scenes.length]);

  const comicStrip = (upTo: number) => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {story.scenes.map((s, i) => (
        <div key={i} className={`rounded-xl border-2 p-3 flex flex-col items-center text-center transition-all duration-500 ${i < upTo ? 'fun-pop bg-white border-rose-200 shadow-md' : 'bg-rose-50/50 border-dashed border-rose-200'}`} style={{ minHeight: 140 }}>
          {i < upTo
            ? <>
                <div className="text-4xl mb-2" aria-hidden="true">{s.art}</div>
                <p className="text-xs text-gray-600 leading-snug">{s.text}</p>
              </>
            : <div className="m-auto text-3xl text-rose-200 font-bold">{i + 1}</div>}
        </div>
      ))}
    </div>
  );

  if (gameState === 'ready') {
    return (
      <GameIntro icon="📖" title="Story Builder" skill="capital letters and punctuation -- Shift, commas, quotes and all." accent="text-rose-600" button="bg-gradient-to-r from-rose-500 to-orange-400" onStart={startGame}>
        Type a silly story one sentence at a time. Every capital letter, comma, question mark and quotation mark has to be exact -- the story won't move on until you hit the right key. Each sentence you finish adds a picture to your comic strip!
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    return (
      <GameOver
        title={`"${story.title}" is complete!`}
        message={accuracy >= 95 ? 'Picture-perfect punctuation!' : 'Great storytelling -- watch those capitals next time!'}
        stars={accuracy >= 95 ? 3 : accuracy >= 85 ? 2 : 1}
        stats={[{ label: 'Speed', value: `${wpm} WPM` }, { label: 'Accuracy', value: `${accuracy}%` }, { label: 'Mistakes', value: errors }]}
        button="bg-gradient-to-r from-rose-500 to-orange-400"
        onRestart={startGame}
      >
        <div className="mt-6 text-left">{comicStrip(story.scenes.length)}</div>
      </GameOver>
    );
  }

  const nextChar = sentence[typed];
  const needsShift = !!nextChar && (/[A-Z!"?:()]/.test(nextChar));

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Sentence" value={`${scene + 1}/${story.scenes.length}`} />
        <StatBox label="Correct Keys" value={correctKeys} />
        <StatBox label="Mistakes" value={errors} />
      </div>
      <div className="rounded-2xl border-4 border-white shadow-inner bg-gradient-to-br from-rose-100 via-orange-50 to-yellow-100 p-4 md:p-6 select-none">
        <h4 className="text-center text-lg font-bold text-rose-700 mb-4"><span aria-hidden="true">{story.cover}</span> {story.title}</h4>
        <div className="bg-white rounded-xl border border-rose-100 shadow-sm px-4 py-6 mb-3 text-center">
          <p className="font-mono text-xl md:text-2xl leading-relaxed break-words">
            {[...sentence].map((ch, i) => (
              <span key={i} className={
                i < typed ? 'text-green-600'
                  : i === typed ? `rounded px-0.5 border-b-4 ${flashError ? 'bg-red-200 border-red-500 text-red-700' : 'bg-yellow-100 border-yellow-400 text-gray-900'}`
                  : 'text-gray-400'}>
                {ch === ' ' && i === typed ? ' ' : ch}
              </span>
            ))}
          </p>
        </div>
        <p className="text-center text-sm h-5 mb-4 text-gray-500">
          {needsShift && <>Hold <span className="font-semibold text-rose-600">Shift</span> for <span className="font-mono font-bold">{nextChar}</span></>}
        </p>
        {comicStrip(scene)}
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Just start typing -- the story waits for the right key.</p>
    </div>
  );
};

export default StoryBuilderGame;
